import { MDXProvider } from "@mdx-js/react";
import { twMerge } from "tailwind-merge";
import { Button, type ButtonVariant } from "@themes/pollinator/ui/Button";
import { useMdxArticleLink } from "./MdxArticleLinkContext";

// MDX often wraps JSX children in a <p> (and 4-space-indented children in a
// <pre>/<code>). Inside a button those block-level wrappers add extra vertical
// space and line breaks, so flatten them to inline content here.
const inlineMdxComponents = {
  p: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  pre: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
  code: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
};

export type Props = {
  /** Article slug. Either `slug` or `id` must be provided. If both are passed, `slug` wins. */
  slug?: string;
  /** Article id (currently equal to slug in this codebase — see useFetchArticle.ts). */
  id?: string;
  /** Themed Button variant. Defaults to "primary". */
  variant?: ButtonVariant;
  iconComponent?: React.ComponentType<{ className?: string }>;
  className?: string;
  children?: React.ReactNode;
};

let warnedAboutMissingProvider = false;

export function SupportArticleButton({
  slug,
  id,
  variant = "primary",
  iconComponent,
  className,
  children,
}: Props) {
  const link = useMdxArticleLink();
  const target = slug ?? id;

  const handleClick = () => {
    if (!target) return;
    if (!link) {
      if (!warnedAboutMissingProvider) {
        warnedAboutMissingProvider = true;
        console.warn(
          "<SupportArticleButton> rendered without an <MdxArticleLinkProvider>; click ignored."
        );
      }
      return;
    }
    link.onArticleSelect(target);
  };

  return (
    <Button
      variant={variant}
      iconComponent={iconComponent}
      className={twMerge(
        // MDX wraps the JSX children in a <p>, which the renderer maps to
        // body2 (text-base font-normal w-full max-w-full mb-4). Reset the
        // box model and inherit typography so the button's height and
        // label style come from the button itself, not the paragraph.
        "[&_p]:m-0 [&_p]:max-w-none [&_p]:[font-size:inherit] [&_p]:[font-weight:inherit] [&_p]:[line-height:inherit]",
        className
      )}
      disabled={!target}
      onClick={handleClick}
      data-testid="support-article-button"
    >
      <MDXProvider components={inlineMdxComponents}>{children}</MDXProvider>
    </Button>
  );
}

export default SupportArticleButton;
